import { backgroundRemovalBatches, removeBackground } from '@/api/remove-background'
// Deep import, NOT the `creation` barrel. Legacy writes
// `import { mediaFramesFromSource } from 'creation'` (utils/get-sticker-from-source.ts:1)
// and gets away with it because app/static's webpack build resolves the whole
// package. Here the barrel (packages/creation/src/index.tsx) transitively pulls in
// the UGE component tree, and uge/components/layer-actions/style.ts:3 imports
// `{ Button, NumberInput } from 'ui'`, which fails to resolve in the production
// Next build — tsc and vitest both resolve it fine, so only `yarn build` catches it.
// Every other file in this app imports creation by deep path for the same reason.
import { mediaFramesFromSource } from 'creation/src/utils/media/frames'
import { FPS } from 'creation/src/creation-tools/constants'
import { CreationToolFrame, CreationToolSourceFile } from 'creation/src/creation-tools/types'

const MAX_FILE_SIZE = 5 // mb, per request
const DOWNSAMPLE_STEP = 0.8

/**
 * Detached opaque regions smaller than this share of the LARGEST region are
 * dropped as background the model failed to remove.
 *
 * Measured against the subject rather than against total opaque area, so the
 * bar scales with the subject instead of drifting as scraps accumulate.
 *
 * The value comes from a real output rather than a guess. A published sticker
 * of a child on a wooden floor decomposed into exactly three regions:
 *
 *     subject     1,209,824 px
 *     floorboard     24,952 px   2.06% of the subject
 *     floorboard      9,677 px   0.80% of the subject
 *
 * An earlier 0.5% bar was set by eye from a scaled-down screenshot and left
 * both fragments in place. 5% clears them with room to spare.
 *
 * The trade-off is real and worth stating: the obvious rule — keep only the
 * largest region — would silently delete a legitimately separate piece, so a
 * ratio is used instead, but a ratio still has a line. A second person or a
 * held object well clear of the body is typically 20%+ of the subject and is
 * safe; something genuinely small and genuinely intended — a thrown ball a long
 * way from the thrower — could fall under 5% and be removed. This is calibrated
 * on one sample. The durable fix is the model not emitting the scraps at all.
 */
const MIN_MASK_REGION_RATIO = 0.05

const preloadImage = (src: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const img = document.createElement('img')
        img.onload = () => resolve(img)
        img.onerror = reject
        img.src = src
    })

const getCurrentFileSize = (urls: string[]): number => {
    let fileSize = 0

    urls.forEach((url) => {
        const bytes = url.length * 1.37
        fileSize += bytes / (1024 * 1024)
    })

    return fileSize
}

/**
 * Size of the largest single request the frames will be split into.
 *
 * This used to measure the WHOLE set against the budget, which quietly
 * destroyed quality on video. The budget is shared, so the per-frame share is
 * the budget divided by the frame count: a still gets the entire 5MB and is
 * never downsampled, while a ten-second video at 30fps gets ~17KB per frame.
 * A 1080x810 frame is ~208KB at the encoder's default quality, so the loop
 * below shrank it by 20% a dozen times over — down to roughly 225x170 — and
 * `applyMask` then scaled the resulting mask back up to full size and
 * thresholded it. The model was segmenting a thumbnail.
 *
 * Frames are sent a batch at a time, so a request is the real unit, and
 * `backgroundRemovalBatches` is the actual splitting rule rather than an
 * approximation of it. Because that rule also caps a batch by encoded length,
 * an oversized set is now split into more requests instead of being shrunk —
 * which is the trade worth making, since request count is cheap and resolution
 * is not. The loop still fires for a single frame large enough to blow the
 * budget on its own, which is the case it should have been guarding all along.
 */
const getLargestRequestSize = (urls: string[]): number =>
    backgroundRemovalBatches(urls).reduce((largest, batch) => Math.max(largest, getCurrentFileSize(batch)), 0)

const decreaseFileSize = async (urls: string[]): Promise<string[]> => {
    const promises: Promise<string>[] = urls.map(
        (url) =>
            new Promise((resolve, reject) => {
                const img = document.createElement('img')
                const canvas = document.createElement('canvas')
                const context = canvas.getContext('2d')
                img.onload = () => {
                    canvas.width = Math.round(img.naturalWidth * DOWNSAMPLE_STEP)
                    canvas.height = Math.round(img.naturalHeight * DOWNSAMPLE_STEP)
                    context?.drawImage(img, 0, 0, canvas.width, canvas.height)
                    resolve(canvas.toDataURL('image/jpeg').split(';base64,')[1])
                }
                img.onerror = reject
                img.src = `data:image/jpeg;base64,${url}`
            })
    )

    return Promise.all(promises)
}

/**
 * Clears opaque regions too small to be part of the subject.
 *
 * `sticker-maker-v3` leaves scraps of background behind — with a photo of a
 * child on a wooden floor, fragments of floorboard survive as islands floating
 * in transparency, attached to nothing. They are unambiguous errors: a piece of
 * a sticker that touches no other piece and covers a fraction of a percent of
 * the subject is not part of it.
 *
 * Only DETACHED regions are addressed. Background still fused to the subject's
 * outline — the ragged fringe along a leg, say — is left alone, because the
 * only way to remove it is to erode the mask inward, and that costs fingertips
 * and hair strands: exactly the detail that sizing frames per request (12.12)
 * just recovered. That fringe is a limit of the model, and is better fixed
 * there than papered over here.
 *
 * Four-connected and iterative. Eight-connectivity would bridge scraps to the
 * subject through a single diagonal pixel, and a recursive flood fill would
 * overflow the stack on a full-resolution frame.
 */
export const removeDetachedMaskRegions = (imageData: ImageData): ImageData => {
    const { data, width, height } = imageData
    const pixelCount = width * height

    let hasOpaquePixel = false
    for (let i = 0; i < pixelCount; i++) {
        if (data[i * 4 + 3] !== 0) {
            hasOpaquePixel = true
            break
        }
    }
    if (!hasOpaquePixel) return imageData

    const labels = new Int32Array(pixelCount).fill(-1)
    const stack = new Int32Array(pixelCount)
    const regionSizes: number[] = []

    for (let seed = 0; seed < pixelCount; seed++) {
        if (data[seed * 4 + 3] === 0 || labels[seed] !== -1) continue

        const label = regionSizes.length
        let top = 0
        let size = 0

        labels[seed] = label
        stack[top++] = seed

        while (top > 0) {
            const pixel = stack[--top]
            size++

            const x = pixel % width
            const y = (pixel - x) / width
            // Each neighbour is labelled as it is pushed, so no pixel enters
            // the stack twice and `stack` cannot exceed `pixelCount`.
            const visit = (neighbour: number) => {
                if (labels[neighbour] === -1 && data[neighbour * 4 + 3] !== 0) {
                    labels[neighbour] = label
                    stack[top++] = neighbour
                }
            }

            if (x > 0) visit(pixel - 1)
            if (x < width - 1) visit(pixel + 1)
            if (y > 0) visit(pixel - width)
            if (y < height - 1) visit(pixel + width)
        }

        regionSizes.push(size)
    }

    const largestRegion = regionSizes.reduce((largest, size) => Math.max(largest, size), 0)
    const minimumSize = largestRegion * MIN_MASK_REGION_RATIO
    for (let i = 0; i < pixelCount; i++) {
        const label = labels[i]
        if (label !== -1 && regionSizes[label] < minimumSize) {
            data[i * 4 + 3] = 0
        }
    }

    return imageData
}

const applyMask = async (frame: CreationToolFrame, mask: string): Promise<CreationToolFrame> => {
    const img = await preloadImage(`data:image/jpeg;base64,${mask}`)
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d', { willReadFrequently: true })
    const frameContext = frame.canvas.getContext('2d', { willReadFrequently: true })

    canvas.width = frame.canvas.width
    canvas.height = frame.canvas.height

    if (context && frameContext) {
        context.drawImage(img, 0, 0, canvas.width, canvas.height)

        const imageData = context.getImageData(0, 0, canvas.width, canvas.height)
        const frameImageData = frameContext.getImageData(0, 0, canvas.width, canvas.height)

        for (let i = 0; i < imageData.data.length; i += 4) {
            const r = imageData.data[i]
            const g = imageData.data[i + 1]
            const b = imageData.data[i + 2]
            const average = (r + g + b) / 3

            imageData.data[i] = frameImageData.data[i]
            imageData.data[i + 1] = frameImageData.data[i + 1]
            imageData.data[i + 2] = frameImageData.data[i + 2]
            imageData.data[i + 3] = Math.round(average / 255) * 255
        }

        context.putImageData(removeDetachedMaskRegions(imageData), 0, 0)
    }

    return { ...frame, canvas }
}

export const getStickerFromCanvas = async (
    canvases: CreationToolFrame[],
    version?: string
): Promise<CreationToolFrame[]> => {
    let encodedFrames = canvases.map(({ canvas }) => canvas.toDataURL('image/jpeg').split(';base64,')[1])
    let currentFileSize = getLargestRequestSize(encodedFrames)

    // downsample until the largest single request fits
    while (currentFileSize > MAX_FILE_SIZE) {
        encodedFrames = await decreaseFileSize(encodedFrames)
        currentFileSize = getLargestRequestSize(encodedFrames)
    }

    const masks = await removeBackground(encodedFrames, version)

    const maskedFrames: CreationToolFrame[] = []

    for (let i = 0; i < masks.length; i++) {
        const masked = await applyMask(canvases[i], masks[i])
        maskedFrames.push(masked)
    }

    return maskedFrames
}

export const getStickerFromSource = async (
    sourceFile: CreationToolSourceFile,
    version?: string
): Promise<CreationToolSourceFile> => {
    const mediaFrames = await mediaFramesFromSource(sourceFile.source, undefined, true)
    const creationFrames: CreationToolFrame[] = mediaFrames.map((frame) => ({
        canvas: frame.canvas,
        frameCount: Math.max(1, Math.round(frame.delay * FPS)),
    }))
    const maskedFrames = await getStickerFromCanvas(creationFrames, version)

    return {
        ...sourceFile,
        frames: maskedFrames,
        hasAudio: false,
        isTransparent: true,
        preview: maskedFrames[0].canvas,
    }
}
